import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProjectImageService } from "@domain/project/project-image.service";
import { ProjectService } from "@domain/project/project.service";
import { TranslateProjectImageTagPipe } from "@domain/project/translate-project-image-tag.pipe";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { AutocompleteSearchFilterPipe } from "@odx/angular/components/autocomplete";
import { User } from "@sentry/angular";
import { filter, map, Observable } from "rxjs";

@Component({
  selector: "app-image-card",
  templateUrl: "./image-card.component.html",
  styleUrls: ["./image-card.component.scss"],
  imports: [UiKitModule, ReactiveFormsModule, TranslateProjectImageTagPipe, AutocompleteSearchFilterPipe],
  standalone: true,
})
export class ImageCardComponent {
  private formBuilder = inject(FormBuilder);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private projectImageService = inject(ProjectImageService);

  @Input() fileUrl!: string;
  @Input() fileName!: string;
  @Input() description?: string;
  @Input() tags?: string[];

  @Output() delete$ = new EventEmitter();
  @Output() download$ = new EventEmitter();
  @Output() submit$ = new EventEmitter<FormGroup>();
  @Output() click$ = new EventEmitter();

  @ViewChild("submitButton")
  submitButton?: ElementRef;

  editForm!: FormGroup;
  editEnabled = false;

  protected user!: User;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;

  protected tagList$: Observable<string[]>;
  protected isMonitoringScope$: Observable<boolean>;

  constructor() {
    const formBuilder = this.formBuilder;

    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    this.editForm = formBuilder.group({
      name: [null, Validators.required],
      description: [""],
      tags: new FormControl<string[] | null>(null),
    });

    this.tagList$ = this.projectImageService.getProjectImageTags(BusinessScope.MONITORING);
    this.isMonitoringScope$ = this.userService.businessScope$.pipe(map((scope) => scope === BusinessScope.MONITORING));
  }

  onEdit() {
    this.hasWritePermission$.subscribe((hasPermission) => {
      if (hasPermission) {
        this.editForm.patchValue({ name: this.fileName, description: this.description, tags: this.tags });
        this.editEnabled = true;
      }
    });
  }

  onDelete() {
    this.hasWritePermission$.subscribe((hasPermission) => {
      if (hasPermission) this.delete$.emit();
    });
  }

  onSubmit() {
    if (!this.editForm.valid) {
      return;
    }
    this.hasWritePermission$.subscribe((hasPermission) => {
      if (hasPermission) {
        this.submit$.emit(this.editForm);
        this.editEnabled = false;
      }
    });
  }

  onClick() {
    this.click$.emit();
  }

  onDownload() {
    this.download$.emit();
  }

  cancelEditMode() {
    this.editEnabled = false;
  }
}
